import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { map, debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';

import { moduleRoutes } from './urlmap';
import { ApplicationHttpClient } from './../http-client';
import { ROLES, EMPTY } from '@utils';
import { IUser, ITag, IWorkbook, IErrorResponse, IResponseBody } from '@interfaces';
import { Role } from '@models';
import { HttpErrorResponse } from '@angular/common/http';
import { ExceptionService } from '@base/exceptions.service';
import { ICompany } from '@base/company/company.structures';
import { getNumber } from '@utils';
import { ResponseBodyAdapter } from '@utils';
import { IResponseCollection, IFilterUsers } from '@user/structure/interfaces';

@Injectable()
export class UserService {
  private mapRoutes = moduleRoutes;
  private _selectedRole: Role;

  private _user: IUser;
  private bsUser = new BehaviorSubject<IUser>(this._user);

  private usersStore: object = {};
  private bsUserStore = new BehaviorSubject<object>(this.usersStore);

  private userWorkbooks: [] = [];
  private bsUserWorkbooks = new BehaviorSubject<[]>(this.userWorkbooks);

  private allowedTags: [] = [];
  private bsAllowedTags = new BehaviorSubject<[]>(this.allowedTags);
  private makingRequest = new BehaviorSubject<boolean>(false);
  private userStatus = new BehaviorSubject<object>({results: null});
  private collection: IResponseCollection;

  constructor(
    private http: ApplicationHttpClient,
    private exceptionService: ExceptionService,
  ) {
    this.collection = {
      count: 0,
      next: null,
      previous: null,
      results: [],
    };

    // inicializa las colecciones...
    Object.keys(ROLES).map(key => {
      const roleName = ROLES[key];
      this.usersStore[roleName] = this.collection;
    });

    this._user = EMPTY;
    this.userWorkbooks = [];
    this.allowedTags = [];
  }

  set selectedRole(role: Role) {
    this._selectedRole = role;
  }

  get selectedRole(): Role {
    return this._selectedRole;
  }

  getUser(id): Observable<any> {
    return this.http.Get(`${this.mapRoutes.getUser}${id}/`);
  }

  register(user: IUser): Observable<IUser> {
    const auxObject = {...user};
    auxObject.role = this._selectedRole.id;
    auxObject.password = String(Math.round((new Date()).getTime() / 1000));  // epoch time

    return this.http.Post(this.mapRoutes.register, auxObject).pipe(
      map((res: IUser) => res)
    );
  }

  observeUsers(): Observable<object> {
    return this.bsUserStore.asObservable();
  }

  getUsers(queryParams: object = null, userType: string, filter: IFilterUsers = null) {
    let finalEndPoint = this.mapRoutes.getUser;
    let makeRequest = true;

    if (filter !== null) {
      const user = filter.user;
      let userId = 0;

      if (user !== null) {
        userId = (filter.user.hasOwnProperty('user_id')) ? filter.user['user_id'] : filter.user['id'];
        userId = getNumber(userId);
      }

      if (userId > 0 && user !== null) {
        finalEndPoint += `${userId}/${filter.endpoint}/`;
      } else if (userId === 0 && user !== null) {
        makeRequest = false;
      }
    }

    if (makeRequest) {
      this.makingRequest.next(true);
      this.http.Get(`${finalEndPoint}`, queryParams).subscribe(
        (res: IResponseBody) => {
          const users = new ResponseBodyAdapter(userType, res);
          this.usersStore[userType] = users.getResponse();
          this.bsUserStore.next(this.usersStore[userType]);
          this.makingRequest.next(false);
        },
        (err: HttpErrorResponse) => {
          this.makingRequest.next(false);
          const error: IErrorResponse = this.exceptionService.errorHandling(err);
        }
      );
    }
  }

  switchStatus(payload): Observable<any> {
    return this.http.Post(this.mapRoutes.status, payload);
  }

  private updateCompanies(user: IUser) {
    const companies = [...user.companies];
    let newCompanies = [];

    companies.map((company) => {
      if (company['related']) {
        newCompanies = [...newCompanies, company];
      }
    });

    return newCompanies;
  }

  // actualizar perfil completo del usuario ...
  // este metodo se llama desde otro servicio (membership y companyUser)...
  updateUser(user: IUser) {
    const userRole: Role = user.role;
    const auxListUsers = [...this.usersStore[userRole.code]['results']];
    const objIndex = auxListUsers.findIndex(
      ((obj: IUser) => obj.id === user.id)
    );
    auxListUsers[objIndex] = user;

    // actualizar el store de los usuarios...
    this.usersStore[userRole.code]['results'] = auxListUsers;
    // this.bsUserStore.next(this.usersStore[userRole.code]);
  }

  updateUserState(user: IUser): void {
    const auxListUsers = [...this.usersStore[this._selectedRole.code]['results']];
    const objIndex = auxListUsers.findIndex(
      ((obj: IUser) => obj.id === user.id)
    );

    // actualizar la lista de empresas asociadas
    user.companies = this.updateCompanies(user);

    // actualizar info y mover el usuario al inicio de la lista...
    auxListUsers[objIndex] = user;
    const userEdited = auxListUsers.splice(objIndex, 1);
    auxListUsers.unshift(userEdited[0]);

    // actualizar el state de los usuarios...
    this.usersStore[this._selectedRole.code] = auxListUsers;
    this.bsUserStore.next(this.usersStore[this._selectedRole.code]);
  }

  update(user: IUser): Observable<any> {
    const auxUser = {...user};
    this.updateUserState(auxUser);
    user.role = auxUser.role['id'];

    return this.http.Put(`${this.mapRoutes.update}${user.id}/`, user).pipe(
      map(res => true)
    );
  }

  delete(user: IUser): Observable<any> {
    return this.http.Delete(`${this.mapRoutes.delete}${user.id}/`).pipe(
      map(response => {
        // actualizar el store de los usuarios...
        const _role = this._selectedRole.code;
        const usersCollection: [] = this.usersStore[_role]['results'];
        const objIndex = usersCollection.findIndex(((obj: IUser) => obj.id === user.id));
        const deleted = usersCollection.splice(objIndex, 1);
        this.bsUserStore.next(this.usersStore[_role]);
        return true;
      })
    );
  }

  userSelectedChanged(): Observable<IUser> {
    return this.bsUser.asObservable();
  }

  setUser(user: IUser): void {
    this._user = user;
    this.bsUser.next(this._user);
  }

  setUsers(bodyUsers: IResponseCollection, role: string): void {
    this.usersStore[role] = bodyUsers;
    this.bsUserStore.next(this.usersStore[role]);
  }

  getCurrentUsers(role: string): IUser[] {
    return this.usersStore[role];
  }

  synchronizeTableau() {
    // actualiza todos los tableau_id de los usuarios existentes en TabelauServer
    // y que aun no tengan id de tablea u en la BD...
    return this.http.Get(this.mapRoutes.sync).pipe(
      map((res: IUser[]) => {
        if (res !== EMPTY) {
          res.map(u => this.updateUser(u));
          return res['results'];
        } else {
          return [];
        }
      },
      (err: HttpErrorResponse) => {
        const error: IErrorResponse = this.exceptionService.errorHandling(err);
      })
    );
  }

  getWorkbooks(user: IUser, company: ICompany) {
    const endpoint = `${this.mapRoutes.getUser}${user.id}/workbooks/`;
    let params: object = EMPTY;

    if  (company !== EMPTY && typeof company !== 'undefined') {
      const companyId = company.hasOwnProperty('pk') ? company['pk'] : company.id;
      params = {company_id: companyId};
    }

    this.http.Get(endpoint, params).subscribe(
      (w: []) => {
        this.userWorkbooks = w;
        this.bsUserWorkbooks.next(w);
      },
      (err: HttpErrorResponse) => {const error: IErrorResponse = this.exceptionService.errorHandling(err); }
    );
  }

  observeUserWorkbooks() {
    return this.bsUserWorkbooks.asObservable();
  }

  getTableauParams(user: IUser) {
    const endpoint = `${this.mapRoutes.getUser}${user.id}/trusted-ticket/`;
    return this.http.Get(endpoint);
  }

  filterWorkbooksByTag(tag: ITag) {
    const auxUserWorkbooks = [...this.userWorkbooks];

    if (tag.id > 0) {
      const filteredUserWorbooks = auxUserWorkbooks.filter((w: IWorkbook) => {
        const hasTag = w.tags.some((item: Object) => item['id'] === tag.id);
        if (hasTag) {return w; }
      });
      this.bsUserWorkbooks.next(filteredUserWorbooks as []);
    } else if (tag.id === 0) {
      this.bsUserWorkbooks.next([...this.userWorkbooks] as []);
    }
  }

  loadTagsAllowed(user: IUser, company: ICompany) {
    if  ((company !== EMPTY && typeof company !== 'undefined') && user.email !== '') {
      const endpoint = `${this.mapRoutes.getUser}${user.id}/tags/`;
      const companyId = company.hasOwnProperty('pk') ? company['pk'] : company.id;
      let params: object = EMPTY;
      params = {company_id: companyId};

      this.http.Get(endpoint, params).subscribe(
        (tags: []) => {
          // notificar a las suscripciones del cambio de tags!
          this.allowedTags = tags['results'];
          this.bsAllowedTags.next(this.allowedTags);
        },
        (err: HttpErrorResponse) => {const error: IErrorResponse = this.exceptionService.errorHandling(err); }
      );
    }
  }

  search(terms: Observable<string>, params: object) {
    return terms.pipe(
        debounceTime(400),
        distinctUntilChanged(),
        switchMap(term => this.searchTerms(term, params))
    );
  }

  searchTerms(term, params) {
    params['search'] = term;

    return this.http.Get(this.mapRoutes.getUser, params)
      .pipe(map(res => res['results'])
    );
  }

  observeAllowedTags() {
    return this.bsAllowedTags.asObservable();
  }

  clearWorkbooks() {
    this.bsAllowedTags.next([]);
    this.bsUserWorkbooks.next([]);
  }

  makingRequestStatus(): Observable<boolean> {
    return this.makingRequest.asObservable();
  }

  obsUserStats(): Observable<object> {
    return this.userStatus.asObservable();
  }

  getUserStatusStats() {
    return this.http.Get(`${this.mapRoutes.getUser}status-summary/`).subscribe(
      (results: object) => this.userStatus.next(results)
    );
  }
}
